import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getProductDetailsCombinationGridKey } from '../../../../services/modules/productDetails/productDetails.keys';
import {
  useCombinationsValidation,
  useProductDetailsCombinations,
  useProductDetailsCount,
} from '../../../../services/modules/productDetails/productDetails.queries';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import { ProductDetailsOriginType } from '../../../../stores/ProductDetails/productDetails.types';
import useIsAdminSeller from '../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import { ESProductSearchState } from '../../details/productDetails.state';
import { ProductDetailsDTO } from '../types/productDetails.types';
import { buildCombinationKey } from '../utils/combinationKey.utils';
import { allocateGroups } from '../utils/productDetailsV2.utils';
import { buildPerPageRequests, mergeOrdersErrors } from '../utils/useProductDetailsList.utils';

interface IProductDetailsListParams extends UsedInOfflineMode {
  product: ESProductSearchState;
}

const useProductDetailsList = ({ product, isOfflineMode = false }: IProductDetailsListParams) => {
  const queryClient = useQueryClient();

  const { productGridParams, relatedProductsFilterState, from } = useProductDetailsStore(
    useShallow((state) => ({
      productGridParams: state.productGridParams,
      relatedProductsFilterState: state.relatedProductsFilterState,
      from: state.from,
    })),
  );
  const isAdminSeller = useIsAdminSeller();
  const catalogId = from?.type === ProductDetailsOriginType.CATALOG ? from.id : undefined;

  const productCombinationsParams = {
    isOfflineMode,
    isAdminSeller,
    ...productGridParams,
    productId: product?.productId,
    catalogId: catalogId,
    selectedProductColorId: product?.productColorId,
    filterByRelatedProducts: relatedProductsFilterState,
    allProductColorIds: product?.allColors.map((color) => color.productColorId) || [],
    limit: 40,
    skip: 0,
  };

  const {
    data: combinationsData,
    isLoading: combinationsLoading,
    fetchNextPage,
    hasNextPage,
  } = useProductDetailsCombinations(productCombinationsParams);

  const combinations = useMemo(
    () =>
      combinationsData?.pages?.reduce(
        (acc, page) => (page.data?.length ? [...acc, ...page.data] : acc),
        [] as ProductDetailsDTO[],
      ),
    [combinationsData?.pages],
  );

  const perPageRequests = buildPerPageRequests(combinationsData?.pages ?? []);

  const getRequests = () =>
    buildPerPageRequests(
      queryClient.getQueryData<{ pages: { data?: ProductDetailsDTO[] }[] }>(
        getProductDetailsCombinationGridKey(productCombinationsParams),
      )?.pages ?? [],
    );

  const { data: validationData, fetchNextPage: fetchNextValidationPage } =
    useCombinationsValidation({
      isOfflineMode,
      queryKeyParams: productCombinationsParams,
      perPageRequests,
      getRequests,
    });

  const ordersErrors = mergeOrdersErrors(validationData?.pages ?? []);

  const getCombinationErrors = (combination: ProductDetailsDTO) => {
    return ordersErrors[combination.orderId ?? '']?.combinationsWithErrors[
      buildCombinationKey(combination)
    ];
  };

  const { data: groupData, isLoading: countLoading } = useProductDetailsCount(
    {
      ...productGridParams,
      isAdminSeller,
      productId: product?.productId,
      catalogId: catalogId,
      filterByRelatedProducts: relatedProductsFilterState,
      allProductColorIds: product?.allColors.map((color) => color.productColorId) || [],
    },
    { enabled: !!combinations },
  );

  const groupCounts = useMemo(() => {
    const groups = allocateGroups(groupData?.groups || [], combinations?.length || 0) || [];

    return groups;
  }, [combinations?.length, groupData?.groups]);

  const fetchMore = useCallback(async () => {
    if (!hasNextPage) {
      return;
    }
    await fetchNextPage();
    fetchNextValidationPage();
  }, [hasNextPage, fetchNextPage, fetchNextValidationPage]);

  return {
    groupCounts,
    combinations,
    combinationsLoading,
    countLoading,
    fetchNextPage: fetchMore,
    getCombinationErrors,
  };
};

export default useProductDetailsList;
